import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  signal,
  ViewChild,
  ElementRef,
  AfterViewInit,
  HostListener,
  OnDestroy,
} from '@angular/core';
import {
  FunnelChartData,
  FunnelStage,
} from '@services/analytics-services/products/models/product-checkout-stats';

@Component({
  selector: 'app-funnel-chart',
  templateUrl: './funnel-chart.component.html',
  styleUrls: ['./funnel-chart.component.scss'],
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        width: 100%;
        height: 100%;
      }
    `,
  ],
  imports: [CommonModule],
  standalone: true,
})
export class FunnelChartComponent implements AfterViewInit, OnDestroy {
  @Input() chartData!: FunnelChartData;
  @ViewChild('funnelSvg', { static: true }) funnelSvg!: ElementRef<SVGSVGElement>;

  // SVG dimensions
  width = 800;
  height = 400;
  margin = { top: 24, right: 20, bottom: 40, left: 40 };
  chartWidth = this.width - this.margin.left - this.margin.right;
  chartHeight = this.height - this.margin.top - this.margin.bottom;
  yAxisLabels = ['0%', '20%', '40%', '60%', '80%', '100%'];
  barGap = 24; // Gap between bars in pixels

  // State signals
  activeStage = signal<FunnelStage | null>(null);
  tooltipPosition = signal<{ x: number; y: number }>({ x: 0, y: 0 });
  showTooltipFlag = signal<boolean>(false);
  private tooltipTimeout: any = null;

  ngAfterViewInit() {
    this.updateChartDimensions();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.updateChartDimensions();
  }

  private updateChartDimensions(): void {
    const containerElement = this.funnelSvg.nativeElement.closest(
      '.funnel-chart-container'
    ) as HTMLElement;
    if (!containerElement) return;

    // Get the container's dimensions
    const containerWidth = containerElement.clientWidth || 800;

    // Calculate appropriate height based on container
    // Use offsetHeight which includes padding
    const containerHeight = containerElement.offsetHeight || 400;

    // Reserve space for header and labels (approximately)
    const headerHeight = 60; // Approximate height of the header
    const labelsHeight = 100; // Approximate height of the labels area

    // Update dimensions based on container
    this.width = containerWidth; // Account for container padding
    this.height = Math.max(containerHeight - headerHeight - labelsHeight, 300); // Ensure minimum height

    // Recalculate chart dimensions
    this.chartWidth = this.width - this.margin.left - this.margin.right;
    this.chartHeight = this.height - this.margin.top - this.margin.bottom;

    // Force update the SVG dimensions
    this.funnelSvg.nativeElement.setAttribute('width', this.width.toString());
    this.funnelSvg.nativeElement.setAttribute('height', this.height.toString());

    // Force a change detection cycle if needed
    setTimeout(() => {
      // This triggers Angular's change detection
    }, 0);
  }

  getBarX(index: number): number {
    const barWidth = this.getBarWidth();
    return index * (barWidth + this.barGap);
  }

  getBarWidth(): number {
    if (!this.chartData || !this.chartData.stages || this.chartData.stages.length === 0) {
      return 0; // Avoid division by zero if stages are not loaded
    }
    const numberOfStages = this.chartData.stages.length;
    if (numberOfStages === 1) {
      return this.chartWidth; // Single bar takes full width
    }
    const totalGapWidth = (numberOfStages - 1) * this.barGap;
    return (this.chartWidth - totalGapWidth) / numberOfStages;
  }

  getBarY(stage: FunnelStage): number {
    return this.chartHeight - this.getBarHeight(stage);
  }

  getBarHeight(stage: FunnelStage): number {
    const baseValue = this.chartData?.stages[0]?.customerCount;
    if (!stage?.customerCount || !baseValue) return 0;
    return (stage.customerCount / baseValue) * this.chartHeight;
  }

  getPercentage(stage: FunnelStage): number {
    // For the first stage, always show 100%
    if (!stage.previousStage) return 100;

    // For subsequent stages, show percentage relative to previous stage
    if (!stage.customerCount || !stage.previousStage.customerCount) return 0;

    return Math.round((stage.customerCount / stage.previousStage.customerCount) * 100);
  }

  getDropOffCount(stage: FunnelStage): string {
    if (!stage.previousStage || !stage.customerCount) return '0';
    const prevCount = stage.previousStage.customerCount || 0;
    return (prevCount - stage.customerCount).toLocaleString();
  }

  getDropOffPercentage(stage: FunnelStage): number {
    if (!stage.previousStage?.customerCount || !stage.customerCount) return 0;
    return Math.round(
      ((stage.previousStage.customerCount - stage.customerCount) /
        stage.previousStage.customerCount) *
        100
    );
  }

  // Calculate the total conversion percentage (relative to first stage)
  getTotalConversionPercentage(stage: FunnelStage): number {
    if (!stage?.customerCount) return 0;
    const baseCount = this.chartData?.stages[0]?.customerCount || 0;
    if (!baseCount) return 0;
    return Math.round((stage.customerCount / baseCount) * 100);
  }

  showTooltip(event: MouseEvent, stage: FunnelStage): void {
    if (this.tooltipTimeout) {
      clearTimeout(this.tooltipTimeout);
    }
    this.activeStage.set(stage);
    this.updateTooltipPosition(event);
    this.showTooltipFlag.set(true);
  }

  private updateTooltipPosition(event: MouseEvent): void {
    const svgRect = this.funnelSvg.nativeElement.getBoundingClientRect();
    const tooltipElement = document.querySelector('.custom-chart-tooltip') as HTMLElement;
    let x = event.clientX - svgRect.left + 10;
    let y = event.clientY - svgRect.top - 50;

    if (tooltipElement) {
      // Adjust position to keep tooltip within viewport
      const tooltipRect = tooltipElement.getBoundingClientRect();
      if (event.clientX + tooltipRect.width + 10 > window.innerWidth) {
        x = event.clientX - svgRect.left - tooltipRect.width - 10;
      }
      if (event.clientY - tooltipRect.height - 50 < 0) {
        y = event.clientY - svgRect.top + 20;
      }
    }

    this.tooltipPosition.set({ x, y });
  }

  hideTooltip(): void {
    this.tooltipTimeout = setTimeout(() => {
      this.showTooltipFlag.set(false);
      this.activeStage.set(null);
    }, 100);
  }

  ngOnDestroy(): void {
    if (this.tooltipTimeout) {
      clearTimeout(this.tooltipTimeout);
      this.tooltipTimeout = null;
    }
  }
}
